"use client"

import posthog from "posthog-js"
import { PostHogProvider as PHProvider } from "posthog-js/react"
import { useEffect, useState } from "react"

/**
 * ?me=1 on any page stops this browser sending anything to PostHog, for good
 * (posthog-js keeps the choice in its own storage); ?me=0 starts it again.
 * It is per browser, so it works on a phone and a laptop, at home or at work,
 * where an IP filter would not. The address is tidied afterwards.
 */
function applyMeSwitch(): string | null {
  const url = new URL(window.location.href)
  const me = url.searchParams.get("me")
  if (me !== "1" && me !== "0") return null
  if (me === "1") posthog.opt_out_capturing()
  else posthog.opt_in_capturing()
  url.searchParams.delete("me")
  window.history.replaceState(null, "", url.pathname + url.search + url.hash)
  return me === "1"
    ? "This browser is no longer tracked. Visit with ?me=0 to undo."
    : "This browser is tracked again."
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const [note, setNote] = useState<string | null>(null)

  useEffect(() => {
    const key = process.env.NEXT_PUBLIC_POSTHOG_KEY
    if (!key) return

    posthog.init(key, {
      // Through our own domain (see next.config.mjs), so ad blockers that
      // block PostHog's domain don't hide visits.
      api_host: "/dx",
      ui_host: "https://eu.posthog.com",
      // PostHogPageView in root layout captures $pageview on every route.
      capture_pageview: false,
      capture_pageleave: true,
      defaults: "2026-01-30",
    })
    const said = applyMeSwitch()
    if (said) {
      setNote(said)
      window.setTimeout(() => setNote(null), 5000)
    }
  }, [])

  return (
    <PHProvider client={posthog}>
      {children}
      {note && (
        <div
          role="status"
          className="fixed bottom-4 left-1/2 z-[100] -translate-x-1/2 rounded-xl bg-[#161B26] px-4 py-3 text-sm text-white shadow-lg"
        >
          {note}
        </div>
      )}
    </PHProvider>
  )
}
